import { useState, type DragEvent } from 'react';
import { baseName, isArchive, splitExtension } from '@aq/shared';
import {
  useMoveFile,
  useMoveFolder,
  useRenameFile,
  useRenameFolder,
  useTrashFile,
  useTrashFolder,
} from '../../lib/queries.ts';
import { useMoveSpreadsheet } from '../../lib/sheet-queries.ts';
import { useMoveDocument } from '../../lib/document-queries.ts';
import { isDriveDrag, readDrag, startDrag, type DragPayload } from './dragging.ts';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateInPlace, payloadFor, useDriveClipboard } from './DriveClipboard.tsx';
import { refreshFolder } from './refreshFolder.ts';
import type { ItemKind } from './useSelection.ts';
import { IconButton, Icons, Spinner, cx } from '../../components/ui.tsx';
import { useDownloads } from './useDownload.ts';
import { ContextMenu, useContextMenu, type MenuItem } from '../../components/ContextMenu.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import { SelectBox } from './SelectionBar.tsx';
import { FileViewer } from './FileViewer.tsx';
import { ShareLinkDialog } from './ShareLinkDialog.tsx';
import { StarButton, useStarMenuItem } from './StarButton.tsx';
import { Thumbnail } from './Thumbnail.tsx';
import { PropertiesPanel } from '../../components/Properties.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { formatBytes, formatDateTime, formatRelative, pluralise } from '../../lib/display.ts';
import { ApiError, api } from '../../lib/api.ts';
import { isImportable, useOpenFileAsSheet } from '../../lib/sheet-queries.ts';
import { isEditableDocument, useDocumentFromFile } from '../../lib/document-queries.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { can } from '@aq/shared';
import { UploadConflict } from './UploadConflict.tsx';
import type { ConflictPolicy } from '../../lib/queries.ts';
import { useNavigate } from 'react-router-dom';
import type { DriveFile, Folder } from '../../lib/types.ts';

type Props = (
  | { kind: 'folder'; folder: Folder; onOpen: () => void }
  | { kind: 'file'; file: DriveFile; onOpen?: never }
) & {
  parentId: string | null;
  selected?: boolean;
  onToggleSelected?: (() => void) | undefined;
  /**
   * Whether anything at all is ticked.
   *
   * Once a selection is under way, clicking a row adds to it rather than
   * opening — which is what every file manager does, and what stops a
   * half-built selection being thrown away by one stray click on the row
   * instead of the box. Navigating away cleared the ticks, so the work of
   * choosing eight files out of forty was lost to a misplaced click.
   */
  selectionActive?: boolean | undefined;
  /**
   * A row in the table, or a tile in the grid.
   *
   * One component either way, deliberately. Everything a row can do — open,
   * rename, drag into a folder, the whole context menu and the dialogs behind
   * it — is the same work in both views, and a second component would be that
   * work written twice, drifting apart by the second bug fix.
   */
  view?: 'list' | 'grid';
};

/**
 * Column widths for every table that lists drive items.
 *
 * Fixed rather than automatic, and declared once so the listing and the
 * search results cannot drift apart. Left to itself the browser hands the
 * name column every spare pixel and squeezes the rest, which wrapped each
 * timestamp onto two lines while a column of short filenames sat in acres of
 * space.
 *
 * Size, Modified and Actions are fixed and sit flush against the right
 * edge; Name is the only column without a width, so it absorbs whatever is
 * left. An earlier version put a spacer column after Actions to pull the
 * group leftward, which left an obvious dead gap along the right-hand side —
 * the columns need to be narrower than the name, not detached from the edge.
 *
 * All three are right-aligned so they read as one block rather than three
 * ragged starts.
 */
export function ItemColumns() {
  return (
    <colgroup>
      {/* The tick box. Narrow and fixed — it holds one control. */}
      <col className="w-10" />
      <col />
      {/* Size and Modified fold into the name cell below md. The width has to
          go to zero as well as the cells being hidden: with table-fixed the
          colgroup reserves the column whether or not anything is drawn in it,
          and 320px of reserved chrome is what pushed the Drive listing off the
          right edge of a phone. */}
      <col className="w-28 max-md:w-0" />
      <col className="w-52 max-md:w-0" />
      <col className="w-28" />
    </colgroup>
  );
}

export function ItemRow(props: Props) {
  const isFolder = props.kind === 'folder';
  const item = isFolder ? props.folder : props.file;
  const file = props.kind === 'file' ? props.file : null;
  const folder = props.kind === 'folder' ? props.folder : null;

  /**
   * The name as shown, without its extension.
   *
   * Explorer's default, and worth matching: the icon already says what the
   * file is, and ".xlsx" repeated down a column of workbooks is noise that
   * pushes the part you are actually reading out of view. The full name is
   * still in the row's tooltip and in Properties.
   */
  const { base: displayName, extension } = splitExtension(item.name);

  const [viewing, setViewing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(displayName);
  const [dropTarget, setDropTarget] = useState(false);
  const [zipping, setZipping] = useState(false);

  const menu = useContextMenu();
  const dialog = useDialog();
  const toast = useToast();
  const downloads = useDownloads();
  const navigate = useNavigate();
  const openAsSheet = useOpenFileAsSheet();
  const openAsDoc = useDocumentFromFile();
  const { user } = useAuth();

  /*
   * Opening a workbook or a letter "for editing" imports it — a new sheet or
   * document is written, and the uploaded file goes to the trash. That is a
   * write, whatever the button says, so a read-only account does not get it
   * and falls through to the viewer instead. Without this the Viewer's very
   * first click on a .docx would be a 403.
   */
  const mayEdit = Boolean(user && can(user.role, 'file:write'));

  /*
   * The other two rights the Drive hands out, checked here as well as on the
   * server.
   *
   * Not for safety — the server refuses either way — but because a button
   * that is always there and sometimes answers "you may not" teaches people
   * to click it and read the red box. Staff no longer delete, and one kind of
   * Viewer no longer downloads; neither should be offered the button.
   */
  const mayDelete = Boolean(user && can(user.role, 'file:delete'));
  const mayDownload = Boolean(user && can(user.role, 'file:download'));

  const renameFolder = useRenameFolder(props.parentId);
  const renameFile = useRenameFile(props.parentId);
  const trashFolder = useTrashFolder(props.parentId);
  const trashFile = useTrashFile(props.parentId);
  const moveFile = useMoveFile(props.parentId);
  const moveFolder = useMoveFolder(props.parentId);
  const moveSpreadsheet = useMoveSpreadsheet();
  const moveDocument = useMoveDocument();
  const clipboard = useDriveClipboard();
  const client = useQueryClient();

  async function commitRename() {
    const typed = draft.trim();
    setRenaming(false);
    if (!typed || typed === displayName) {
      setDraft(displayName);
      return;
    }

    // Only the base was editable, so the extension is put back — renaming a
    // workbook must not quietly turn it into a file with no format.
    const name = `${typed}${extension}`;
    try {
      if (isFolder) await renameFolder.mutateAsync({ id: item.id, name });
      else await renameFile.mutateAsync({ id: item.id, name });
      toast.success(`Renamed to "${typed}"`);
    } catch (caught) {
      setDraft(displayName); // put the old name back so the row never lies
      toast.error(
        'Could not rename',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  /**
   * Take a dropped row into this folder.
   *
   * Only folders accept a drop, and never their own row — dragging a folder
   * into itself is the one move that would detach a subtree from the tree.
   * The server refuses it as well; catching it here is what keeps the cursor
   * honest about whether the drop will do anything.
   */
  async function acceptDrop(payload: DragPayload) {
    if (!folder || (payload.kind === 'folder' && payload.id === folder.id)) return;

    try {
      const target = folder.id;
      if (payload.kind === 'file') await moveFile.mutateAsync({ id: payload.id, targetId: target });
      else if (payload.kind === 'folder')
        await moveFolder.mutateAsync({ id: payload.id, targetId: target });
      else if (payload.kind === 'spreadsheet')
        await moveSpreadsheet.mutateAsync({ id: payload.id, folderId: target });
      else await moveDocument.mutateAsync({ id: payload.id, folderId: target });

      toast.success(`Moved "${baseName(payload.name)}" into "${displayName}"`);
    } catch (caught) {
      toast.error(
        'Could not move that',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function trash() {
    const confirmed = await dialog.confirm({
      title: `Move "${item.name}" to the trash?`,
      message: isFolder
        ? 'Everything inside this folder goes with it. Nothing is destroyed — trashed items can be restored.'
        : 'Nothing is destroyed — trashed items can be restored.',
      confirmLabel: 'Move to trash',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      if (isFolder) await trashFolder.mutateAsync(item.id);
      else await trashFile.mutateAsync(item.id);
      toast.success(`"${item.name}" moved to the trash`);
    } catch (caught) {
      toast.error(
        'Could not move to the trash',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  /**
   * Download, with a card that reports the bytes as they land.
   *
   * See lib/download.ts: this is a fetch rather than a link because it needs
   * an auth header, which costs the browser's own download indicator — so the
   * progress has to be drawn here or there is none anywhere.
   */
  async function download() {
    if (!file) return;
    await downloads.file(item.id, item.name);
  }

  const kind: ItemKind = isFolder ? 'folder' : 'file';

  /** A copy beside the original, named "… (1)". */
  async function duplicate() {
    try {
      await duplicateInPlace(payloadFor(kind, item.id), props.parentId);
      await refreshFolder(client, props.parentId);
      toast.success(`"${displayName}" duplicated`);
    } catch (caught) {
      toast.error(
        'Could not duplicate that',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  /** Paste the clipboard into this folder. */
  async function pasteInto() {
    if (!folder) return;
    try {
      const done = await clipboard.paste(folder.id);
      if (!done) return;
      await refreshFolder(client, props.parentId);
      toast.success(
        `${done.count === 1 ? 'Item' : `${done.count} items`} ${done.mode === 'cut' ? 'moved' : 'copied'} into "${displayName}"`,
      );
    } catch (caught) {
      toast.error(
        'Could not paste there',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  /**
   * The whole folder, as one zip.
   *
   * Everything inside it and everything inside those, with the tree preserved:
   * a year of paperwork filed by month is only useful unzipped as a year of
   * paperwork filed by month. Sheets and documents come out as .xlsx and
   * .docx, so what unpacks is a folder of things that open.
   */
  /**
   * Unpack a zip into a folder beside it.
   *
   * Reported as it goes because it is not instant — every file inside is
   * scanned and stored exactly as an upload would be, and forty of them takes
   * as long as uploading forty of them. Silence here would be the Download
   * button all over again.
   */
  async function extract() {
    if (!file) return;
    const card = toast.progress(`Extracting "${displayName}"`, 'Reading the archive…');
    setZipping(true);
    try {
      const result = await api.post<{
        folderName: string;
        extracted: number;
        skipped: { name: string; reason: string }[];
      }>(`/api/drive/files/${file.id}/extract`, {});

      await refreshFolder(client, props.parentId);
      card.done(
        `Extracted into "${result.folderName}"`,
        result.skipped.length === 0
          ? `${pluralise(result.extracted, 'file')}. The archive itself is untouched.`
          : `${pluralise(result.extracted, 'file')}, and ${result.skipped.length} skipped — ${result.skipped[0]?.reason}.`,
      );
    } catch (caught) {
      card.fail(
        'Could not extract that archive',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    } finally {
      setZipping(false);
    }
  }

  async function downloadFolder() {
    if (!folder) return;
    setZipping(true);
    try {
      await downloads.archive({ folderIds: [folder.id] }, displayName);
    } finally {
      setZipping(false);
    }
  }

  /**
   * Convert a workbook into an editable sheet.
   *
   * The file itself is left alone. Importing produces a separate document, so
   * the uploaded .xlsx moves to the trash, so the Drive shows one thing
   * rather than a file and a sheet with the same name and no way to tell
   * which anyone should be editing. It stays recoverable, and the sheet
   * exports back to .xlsx at any point.
   */
  async function openAsSpreadsheet() {
    if (!file) return;
    try {
      const created = await openAsSheet.mutateAsync({ fileId: file.id });
      toast.success(
        `"${created.name}" is now a sheet`,
        'The uploaded file has moved to the trash — carry on working in the sheet.',
      );
      navigate(`/sheets/${created.id}`);
    } catch (caught) {
      toast.error(
        'Could not open that workbook',
        caught instanceof ApiError ? caught.message : 'The file may be corrupt.',
      );
    }
  }

  /**
   * Open a Word file as the letter it is.
   *
   * The same bargain as a workbook: importing produces a document, the
   * uploaded file goes to the trash so the Drive shows one thing rather than
   * two with the same name, and it exports back to .docx whenever it is
   * wanted. Until now a .docx did neither — it went to the viewer, which can
   * only show a PDF rendering, so the client opened a letter and got a
   * picture of one.
   */
  async function openAsDocument(onConflict?: ConflictPolicy) {
    if (!file) return;
    try {
      const created = await openAsDoc.mutateAsync({
        fileId: file.id,
        ...(onConflict ? { onConflict } : {}),
      });
      toast.success(
        `"${created.name}" is now a document`,
        'The uploaded file has moved to the trash — carry on working in the document.',
      );
      navigate(`/documents/${created.id}`);
    } catch (caught) {
      /*
       * A name the folder already holds is a question, not a failure.
       *
       * The Drive has asked it about uploads for months — keep both, replace,
       * or leave it — and adding the same Word file twice was the one place
       * that answered with a flat refusal instead. Same dialog, same three
       * answers, so it reads the same wherever the collision happens.
       */
      if (caught instanceof ApiError && caught.status === 409) {
        askAboutName(splitExtension(item.name).base);
        return;
      }
      toast.error(
        'Could not open that document',
        caught instanceof ApiError ? caught.message : 'The file may be corrupt.',
      );
    }
  }

  /** The three answers, then try again with whichever was chosen. */
  function askAboutName(wanted: string) {
    dialog.open((close) => (
      <UploadConflict
        filename={item.name}
        check={{ taken: true, suggestion: `${wanted} (1)`, kind: 'document' }}
        remaining={0}
        action="open"
        onChoose={(choice) => {
          if (choice) void openAsDocument(choice);
        }}
        onClose={close}
      />
    ));
  }

  /**
   * The link dialog, raised through the dialog service like every other one
   * here — so it lands in the same place, closes on Escape, and holds the page
   * still behind it without this component knowing how any of that works.
   */
  function showLinkDialog() {
    dialog.open((close) => (
      <ShareLinkDialog
        kind={isFolder ? 'FOLDER' : 'FILE'}
        targetId={item.id}
        name={item.name}
        onClose={close}
      />
    ));
  }

  function showProperties() {
    dialog.open((close) => (
      <PropertiesPanel
        onClose={close}
        content={{
          name: item.name,
          kind: isFolder ? 'Folder' : describeMimeType(file?.mimeType ?? ''),
          icon: isFolder ? (
            <BrandIcon src={BRAND.folder} size={24} />
          ) : (
            <FileTypeIcon name={item.name} mimeType={file?.mimeType ?? ''} size={24} />
          ),
          rows: [
            ...(file
              ? [
                  { label: 'Size', value: formatBytes(file.sizeBytes), numeric: true },
                  { label: 'Type', value: file.mimeType || 'Unknown', numeric: false },
                  {
                    label: 'Virus scan',
                    value: <ScanBadge status={file.scanStatus} />,
                  },
                ]
              : [{ label: 'Location', value: folder?.path ?? '/', numeric: false }]),
            { label: 'Created', value: formatDateTime(item.createdAt), numeric: true },
            {
              label: 'Modified',
              value: `${formatDateTime(item.updatedAt)} (${formatRelative(item.updatedAt)})`,
              numeric: true,
            },
            { label: 'Starred', value: item.isStarred ? 'Yes' : 'No' },
            { label: 'Identifier', value: item.id, numeric: true },
          ],
        }}
      />
    ));
  }

  /**
   * What clicking this file should do.
   *
   * A workbook or a Word file opens for editing — it is here to be worked on,
   * and a PDF rendering of it is a picture of the thing rather than the thing.
   * Everything else opens in the viewer, which shows PDFs, images and video as
   * themselves, text as text, and says plainly when it cannot show something
   * rather than presenting a broken frame.
   */
  const open = () => {
    if (mayEdit && file && isImportable(file.name)) {
      void openAsSpreadsheet();
      return;
    }
    if (mayEdit && file && isEditableDocument(file.name)) {
      void openAsDocument();
      return;
    }
    setViewing(true);
  };

  /** Starring, as a line in the menu — see useStarMenuItem. */
  const starItem = useStarMenuItem(
    isFolder ? 'folder' : 'file',
    item.id,
    item.name,
    item.isStarred,
  );

  const menuItems: MenuItem[] = [
    ...(isFolder
      ? [
          { label: 'Open', icon: <Icons.Folder />, onSelect: () => props.onOpen() },
          ...(mayEdit
            ? [
                {
                  label: 'Share a link…',
                  icon: <Icons.Copy />,
                  onSelect: () => showLinkDialog(),
                },
              ]
            : []),
          starItem,
          ...(mayDownload
            ? [
                {
                  label: 'Download as zip',
                  icon: <Icons.Download />,
                  onSelect: () => void downloadFolder(),
                },
              ]
            : []),
        ]
      : [
          {
            label:
              mayEdit && file && (isImportable(file.name) || isEditableDocument(file.name))
                ? 'Open for editing'
                : 'Open',
            icon:
              file && isImportable(file.name) ? (
                <GridGlyph />
              ) : file && isEditableDocument(file.name) ? (
                <PageGlyph />
              ) : (
                <Icons.Eye />
              ),
            onSelect: open,
          },
          ...(mayDownload
            ? [{ label: 'Download', icon: <Icons.Download />, onSelect: () => void download() }]
            : []),
          ...(mayEdit
            ? [
                {
                  label: 'Share a link…',
                  icon: <Icons.Copy />,
                  onSelect: () => showLinkDialog(),
                },
              ]
            : []),
          starItem,

          /*
           * A zip is the one file type where "Open" cannot mean anything and
           * the useful verb is a different one. Offered only on an archive,
           * and only to an account that may write, because it creates a
           * folder and everything in it.
           */
          ...(mayEdit && file && isArchive(file.name, file.mimeType)
            ? [
                {
                  label: 'Extract here',
                  icon: <Icons.Archive />,
                  onSelect: () => void extract(),
                },
              ]
            : []),
        ]),
    {
      label: 'Rename',
      icon: <Icons.Pencil />,
      shortcut: 'F2',
      onSelect: () => {
        setDraft(displayName);
        setRenaming(true);
      },
    },
    {
      label: 'Duplicate',
      icon: <Icons.Copy />,
      shortcut: 'Ctrl+D',
      onSelect: () => void duplicate(),
      separatorBefore: true,
    },
    {
      label: 'Copy',
      icon: <Icons.Copy />,
      shortcut: 'Ctrl+C',
      onSelect: () => clipboard.copy(payloadFor(kind, item.id)),
    },
    {
      label: 'Cut',
      icon: <Icons.Scissors />,
      shortcut: 'Ctrl+X',
      onSelect: () => clipboard.cut(payloadFor(kind, item.id)),
    },
    // Pasting onto a folder puts things inside it, which is what dropping on
    // one does too. There is nowhere to paste "into" a file.
    ...(isFolder && clipboard.clipboard
      ? [
          {
            label: `Paste ${clipboard.clipboard.count === 1 ? 'item' : `${clipboard.clipboard.count} items`} here`,
            icon: <Icons.Clipboard />,
            onSelect: () => void pasteInto(),
            disabled: clipboard.pasting,
          },
        ]
      : []),
    {
      label: 'Properties',
      icon: <InfoGlyph />,
      shortcut: 'Alt+Enter',
      onSelect: showProperties,
      separatorBefore: true,
    },
    ...(mayDelete
      ? [
          {
            label: 'Move to trash',
            icon: <Icons.Trash />,
            shortcut: 'Del',
            tone: 'danger' as const,
            onSelect: () => void trash(),
            separatorBefore: true,
          },
        ]
      : []),
  ];

  /** What a click on the body of a row or a tile means. */
  const activate = (event: React.MouseEvent) => {
    if (renaming) return;
    // Anything with its own behaviour — the tick box, the action buttons, the
    // name button — handles its own click.
    if ((event.target as HTMLElement).closest('button, input, a, label')) return;

    // Picking, not opening: the second click of a selection should extend it,
    // not navigate away from the rows being chosen.
    if (props.selectionActive) {
      props.onToggleSelected?.();
      return;
    }

    if (isFolder) props.onOpen();
    else open();
  };

  /** Dragging, which is the same work for a row and for a tile. */
  const dragging = {
    draggable: !renaming,
    onDragStart: (event: DragEvent) =>
      startDrag(event, { kind: isFolder ? 'folder' : 'file', id: item.id, name: item.name }),
    onDragOver: (event: DragEvent) => {
      if (!isFolder || !isDriveDrag(event)) return;
      // Both are required, and for different reasons: preventDefault on
      // dragover is what makes an element a drop target at all, and
      // stopPropagation keeps the page-level "drop files to upload" overlay
      // from also claiming this drag.
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'move';
      setDropTarget(true);
    },
    onDragLeave: () => setDropTarget(false),
    onDrop: (event: DragEvent) => {
      if (!isFolder || !isDriveDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setDropTarget(false);
      const payload = readDrag(event);
      if (payload) void acceptDrop(payload);
    },
  };

  /** The dialogs and overlays a row and a tile both carry. */
  const attached = (
    <>
      {menu.position ? (
        <ContextMenu items={menuItems} position={menu.position} onClose={menu.close} />
      ) : null}

      {viewing && file ? (
        <FileViewer
          fileId={file.id}
          name={file.name}
          mimeType={file.mimeType}
          onClose={() => setViewing(false)}
          {...(mayDownload ? { onDownload: () => void download() } : {})}
          {...(mayEdit && isImportable(file.name)
            ? { onExtract: () => void openAsSpreadsheet() }
            : {})}
        />
      ) : null}
    </>
  );

  if (props.view === 'grid') {
    return (
      <>
        <li
          {...dragging}
          onContextMenu={menu.openAtPointer}
          onClick={activate}
          className={cx(
            'group relative flex cursor-pointer flex-col gap-2 rounded-xl border p-2.5 transition-fast',
            props.selected
              ? 'border-[var(--blue-600)] bg-blue-50'
              : 'border-border bg-surface hover:bg-blue-50',
            dropTarget &&
              'border-blue-600 bg-blue-100 outline outline-2 -outline-offset-2 outline-blue-600',
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {props.onToggleSelected ? (
              <span
                className={cx(
                  'shrink-0 transition-fast',
                  // Out of the way until it is wanted, which is what keeps a
                  // grid of tiles looking like pictures rather than a form.
                  props.selected || props.selectionActive
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100',
                )}
              >
                <SelectBox
                  checked={Boolean(props.selected)}
                  label={`Select ${item.name}`}
                  onChange={props.onToggleSelected}
                />
              </span>
            ) : null}

            <span className="shrink-0" aria-hidden>
              {isFolder ? (
                <BrandIcon src={BRAND.folder} size={20} />
              ) : (
                <FileTypeIcon name={item.name} mimeType={file?.mimeType ?? ''} />
              )}
            </span>

            {renaming ? (
              <input
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => void commitRename()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void commitRename();
                  if (event.key === 'Escape') {
                    setDraft(displayName);
                    setRenaming(false);
                  }
                }}
                aria-label={`Rename ${item.name}`}
                className="h-8 min-w-0 flex-1 rounded-md border border-border-input px-2 text-body-sm text-ink"
              />
            ) : (
              <button
                type="button"
                onClick={isFolder ? props.onOpen : open}
                title={item.name}
                className={cx(
                  'min-w-0 flex-1 truncate text-left text-body-sm text-ink',
                  isFolder && 'font-medium',
                )}
              >
                {displayName}
              </button>
            )}

            <StarButton
              kind={isFolder ? 'folder' : 'file'}
              id={item.id}
              name={item.name}
              starred={item.isStarred}
            />
            <IconButton label={`Actions for ${item.name}`} onClick={menu.openUnder}>
              <KebabGlyph />
            </IconButton>
          </div>

          {/*
            The tile's picture.

            A folder has nothing to show and shows what it is instead — and so
            does a file whose thumbnail has not been rendered, or never will
            be. A picture of a spreadsheet is no more use than the word XLSX.
          */}
          <div className="flex h-32 items-center justify-center overflow-hidden rounded-lg bg-sunken">
            {file ? (
              <Thumbnail fileId={file.id} name={file.name} mimeType={file.mimeType} />
            ) : (
              <BrandIcon src={BRAND.folder} size={44} />
            )}
          </div>

          <p className="truncate text-micro text-muted">
            {file ? `${formatBytes(file.sizeBytes)} · ` : ''}
            {formatRelative(item.updatedAt)}
          </p>
        </li>

        {attached}
      </>
    );
  }

  return (
    <>
      <tr
        className={cx(
          'group h-row cursor-pointer border-b border-border transition-fast last:border-0 hover:bg-blue-50',
          props.selected && 'bg-blue-50',
          // A folder under the pointer during a drag: the ring is the only
          // thing telling you which of ten rows will catch the drop.
          dropTarget && 'bg-blue-100 outline outline-2 -outline-offset-2 outline-blue-600',
        )}
        onContextMenu={menu.openAtPointer}
        // The whole row opens, not just the name. Clicking a filename-sized
        // target in a full-width row is a small aim for no reason.
        onClick={(event) => {
          if (renaming) return;
          // Anything with its own behaviour — the tick box, the action
          // buttons, the name button — handles its own click.
          if ((event.target as HTMLElement).closest('button, input, a, label')) return;

          // Picking, not opening: the second click of a selection should
          // extend it, not navigate away from the rows being chosen.
          if (props.selectionActive) {
            props.onToggleSelected?.();
            return;
          }

          if (isFolder) props.onOpen();
          else open();
        }}
        draggable={!renaming}
        onDragStart={(event: DragEvent) =>
          startDrag(event, {
            kind: isFolder ? 'folder' : 'file',
            id: item.id,
            name: item.name,
          })
        }
        onDragOver={(event: DragEvent) => {
          if (!isFolder || !isDriveDrag(event)) return;
          // Both are required, and for different reasons: preventDefault on
          // dragover is what makes an element a drop target at all, and
          // stopPropagation keeps the page-level "drop files to upload"
          // overlay from also claiming this drag.
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'move';
          setDropTarget(true);
        }}
        onDragLeave={() => setDropTarget(false)}
        onDrop={(event: DragEvent) => {
          if (!isFolder || !isDriveDrag(event)) return;
          event.preventDefault();
          event.stopPropagation();
          setDropTarget(false);
          const payload = readDrag(event);
          if (payload) void acceptDrop(payload);
        }}
      >
        <td className="pl-4">
          {props.onToggleSelected ? (
            <SelectBox
              checked={Boolean(props.selected)}
              label={`Select ${item.name}`}
              onChange={props.onToggleSelected}
            />
          ) : null}
        </td>

        <td className="px-2 md:px-4">
          <div className="flex min-w-0 items-center gap-2.5 md:gap-3">
            <span className="shrink-0" aria-hidden>
              {isFolder ? (
                <BrandIcon src={BRAND.folder} size={20} />
              ) : (
                <FileTypeIcon name={item.name} mimeType={file?.mimeType ?? ''} />
              )}
            </span>

            {renaming ? (
              <input
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => void commitRename()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void commitRename();
                  if (event.key === 'Escape') {
                    setDraft(displayName);
                    setRenaming(false);
                  }
                }}
                aria-label={`Rename ${item.name}`}
                className="h-8 flex-1 rounded-md border border-border-input px-2 text-body-sm text-ink"
              />
            ) : (
              <button
                type="button"
                onClick={isFolder ? props.onOpen : open}
                // The tooltip carries the full name including the extension,
                // which the label deliberately drops.
                title={item.name}
                className={cx(
                  'truncate rounded-sm text-left text-body-sm text-ink hover:text-blue-700 hover:underline',
                  isFolder && 'font-medium',
                )}
              >
                {displayName}
              </button>
            )}

            {file && file.scanStatus !== 'CLEAN' ? (
              <span className="rounded-full bg-warning-bg px-2 py-0.5 text-micro font-medium text-warning">
                {file.scanStatus === 'PENDING' ? 'Scanning' : 'Not scanned'}
              </span>
            ) : null}
          </div>

          {/* The two columns that were dropped, returned as a second line.
              Hidden rather than removed on desktop, where they have their own
              columns and repeating them here would just be noise. */}
          <p className="mt-0.5 truncate pl-[30px] text-micro text-muted md:hidden">
            {file ? `${formatBytes(file.sizeBytes)} · ` : ''}
            {formatDateTime(item.updatedAt)}
          </p>
        </td>

        <td
          className="whitespace-nowrap px-4 text-right text-body-sm text-muted max-md:hidden"
          data-numeric
        >
          {file ? formatBytes(file.sizeBytes) : '—'}
        </td>

        <td
          className="whitespace-nowrap px-4 text-right text-body-sm text-muted max-md:hidden"
          data-numeric
        >
          {formatDateTime(item.updatedAt)}
        </td>

        <td className="px-4">
          {/* Always visible. Hiding these until hover meant nobody found them
              without sweeping the mouse across every row first. */}
          <div className="flex items-center justify-end gap-0.5">
            {mayEdit && file && isImportable(file.name) ? (
              <Tooltip
                label="Convert to sheet"
                detail="Turns this workbook into an editable sheet. The uploaded file moves to the trash."
              >
                <IconButton
                  label={`Convert ${item.name} to a sheet`}
                  onClick={() => void openAsSpreadsheet()}
                  disabled={openAsSheet.isPending}
                >
                  <GridGlyph />
                </IconButton>
              </Tooltip>
            ) : null}
            {mayEdit && file && isEditableDocument(file.name) ? (
              <Tooltip
                label="Open for editing"
                detail="Opens this letter in the editor. The uploaded file moves to the trash."
              >
                <IconButton
                  label={`Open ${item.name} for editing`}
                  onClick={() => void openAsDocument()}
                  disabled={openAsDoc.isPending}
                >
                  <PageGlyph />
                </IconButton>
              </Tooltip>
            ) : null}
            {file && !(mayEdit && (isImportable(file.name) || isEditableDocument(file.name))) ? (
              <Tooltip label="Open" detail="View this file without downloading it">
                <IconButton label={`Open ${item.name}`} onClick={() => setViewing(true)}>
                  <Icons.Eye />
                </IconButton>
              </Tooltip>
            ) : null}
            {file && mayDownload ? (
              <Tooltip label="Download">
                <IconButton label={`Download ${item.name}`} onClick={() => void download()}>
                  <Icons.Download />
                </IconButton>
              </Tooltip>
            ) : null}
            {isFolder && mayDownload ? (
              <Tooltip
                label="Download as zip"
                detail="Everything in this folder, keeping its structure."
              >
                <IconButton
                  label={`Download ${item.name} as a zip`}
                  onClick={() => void downloadFolder()}
                  disabled={zipping}
                >
                  {zipping ? <Spinner label="Preparing" /> : <Icons.Download />}
                </IconButton>
              </Tooltip>
            ) : null}
            <StarButton
              kind={isFolder ? 'folder' : 'file'}
              id={item.id}
              name={item.name}
              starred={item.isStarred}
            />
            <IconButton label={`Actions for ${item.name}`} onClick={menu.openUnder}>
              <KebabGlyph />
            </IconButton>
          </div>
        </td>
      </tr>

      {menu.position ? (
        <ContextMenu items={menuItems} position={menu.position} onClose={menu.close} />
      ) : null}

      {viewing && file ? (
        <FileViewer
          fileId={file.id}
          name={file.name}
          mimeType={file.mimeType}
          onClose={() => setViewing(false)}
          {...(mayDownload ? { onDownload: () => void download() } : {})}
          {...(mayEdit && isArchive(file.name, file.mimeType)
            ? {
                onExtract: () => {
                  // The viewer has nothing to show once this starts, and the
                  // progress is reported on a toast over the listing.
                  setViewing(false);
                  void extract();
                },
              }
            : {})}
        />
      ) : null}
    </>
  );
}

function ScanBadge({ status }: { status: DriveFile['scanStatus'] }) {
  const label =
    status === 'CLEAN'
      ? 'Clean'
      : status === 'PENDING'
        ? 'Scanning…'
        : status === 'INFECTED'
          ? 'Infected — blocked'
          : 'Scan failed';

  return (
    <span
      className={cx(
        'inline-block rounded-full px-2 py-0.5 text-micro font-medium',
        status === 'CLEAN'
          ? 'bg-success-bg text-[var(--success)]'
          : status === 'PENDING'
            ? 'bg-[var(--blue-50)] text-[var(--blue-700)]'
            : 'bg-danger-bg text-[var(--danger)]',
      )}
    >
      {label}
    </span>
  );
}

/** Plain words for a MIME type, the way a file explorer names them. */
function describeMimeType(mime: string): string {
  const known: Record<string, string> = {
    'application/pdf': 'PDF document',
    'text/plain': 'Text document',
    'text/csv': 'CSV file',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel workbook',
    'application/vnd.ms-excel': 'Excel workbook (legacy)',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
    'application/msword': 'Word document (legacy)',
    'application/zip': 'Archive',
  };
  if (known[mime]) return known[mime] as string;
  if (mime.startsWith('image/')) return `${mime.slice(6).toUpperCase()} image`;
  return mime || 'File';
}

function GridGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="2.5" y="2.5" width="13" height="13" rx="1.5" />
      <path d="M2.5 6.5h13M2.5 10.5h13M7 6.5v9M11.5 6.5v9" />
    </svg>
  );
}

/** A sheet of writing, to the grid's ruled squares. */
function PageGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <path d="M4 1.5h6.5L14.5 5.5V16.5H4z" strokeLinejoin="round" />
      <path d="M10.5 1.5v4h4M6.5 8.5h5M6.5 11.5h5M6.5 14h3" strokeLinecap="round" />
    </svg>
  );
}

function KebabGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function InfoGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7.2v4" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}
